import {
  Component,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  inject,
  DestroyRef,
  afterNextRender,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { ActionOption } from '../types';
import { ZagButton } from '../../button/button';
import { ZagIcon } from '../../icon/icon';
import { Subject } from 'rxjs';

// Global subject to coordinate dropdown state across all instances
const dropdownOpenSubject = new Subject<CardViewActionButtonComponent>();

@Component({
  selector: 'zag-card-view-action-button',
  imports: [TranslateModule, ZagButton, ZagIcon],
  templateUrl: './card-view-action-button.component.html',
  styleUrls: ['./card-view-action-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardViewActionButtonComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    // Listen for other dropdowns opening
    const subscription = dropdownOpenSubject.subscribe(openedComponent => {
      // Close this dropdown if another one opened
      if (openedComponent !== this && this.dropdownOpen()) {
        this.closeDropdown();
        this.cdr.markForCheck();
      }
    });

    // Set up document click listener after render
    afterNextRender(() => {
      const clickListener = (event: MouseEvent) => {
        if (!this.elementRef.nativeElement.contains(event.target)) {
          this.closeDropdown();
          this.cdr.markForCheck();
        }
      };

      document.addEventListener('click', clickListener);

      this.destroyRef.onDestroy(() => {
        document.removeEventListener('click', clickListener);
        subscription.unsubscribe();
      });
    });
  }
  /**
   * Accessible label for the button
   */
  label = input.required<string>();

  /**
   * Currently selected option ID
   */
  currentValue = input.required<string>();

  /**
   * Available options
   */
  options = input.required<ActionOption[]>();

  /**
   * Optional dropdown header title
   */
  dropdownTitle = input<string>();

  /**
   * Emitted when an option is selected
   */
  optionSelected = output<string>();

  /**
   * Whether the dropdown is open
   */
  dropdownOpen = signal<boolean>(false);

  /**
   * Get the currently selected option
   */
  get currentOption(): ActionOption | undefined {
    return this.options().find(o => o.id === this.currentValue());
  }

  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(event: Event): void {
    event.stopPropagation();
    const willBeOpen = !this.dropdownOpen();
    this.dropdownOpen.set(willBeOpen);

    // Notify other instances if opening
    if (willBeOpen) {
      dropdownOpenSubject.next(this);
    }
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  /**
   * Select an option
   */
  selectOption(optionId: string, event: Event): void {
    event.stopPropagation();
    this.optionSelected.emit(optionId);
    this.closeDropdown();
  }
}
